import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, keyOf } = useCart();
  const navigate = useNavigate();
  const total = subtotal;

  if (items.length === 0) {
    return (
      <div className="container-ping py-32 text-center" data-testid="empty-cart">
        <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground" strokeWidth={1} />
        <h1 className="heading-2 mt-6">Your Bag Is Empty</h1>
        <p className="text-muted-foreground mt-2">Add some gear from the Pro Shop to get started.</p>
        <Link to="/shop" className="mt-8 inline-block bg-[#0A0A0A] text-white px-10 py-4 label-caps btn-transition hover:bg-black/80 active:scale-95" data-testid="continue-shopping">
          Shop Now
        </Link>
      </div>
    );
  }

  return (
    <div className="container-ping py-14" data-testid="cart-page">
      <h1 className="heading-1 mb-10">Your Bag</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-12">
        <div className="divide-y divide-border border-t border-b border-border">
          {items.map((item) => {
            const k = keyOf(item.product_id, item.variant, item.customization);
            return (
              <div key={k} className="flex gap-4 py-6" data-testid={`cart-item-${k}`}>
                <img src={item.image} alt={item.name} className="w-24 h-24 object-cover knockout-white bg-[#0A0A0A] border border-border" />
                <div className="flex-1">
                  <h3 className="font-head text-lg font-bold uppercase tracking-tight leading-tight">{item.name}</h3>
                  {item.variant && <p className="text-xs text-muted-foreground mt-1">{item.variant}</p>}
                  {item.customization && <p className="text-xs text-muted-foreground mt-0.5" data-testid={`cart-custom-${k}`}>Personalization: “{item.customization}”</p>}
                  <p className="text-muted-foreground text-sm mt-1">{formatINR(item.price)}</p>
                  <div className="mt-3 flex items-center gap-4">
                    <div className="flex items-center border border-border">
                      <button onClick={() => updateQty(k, item.quantity - 1)} className="px-3 py-2 hover:bg-secondary btn-transition" data-testid={`cart-decrease-${k}`} aria-label="Decrease">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="px-4 text-sm font-bold">{item.quantity}</span>
                      <button onClick={() => updateQty(k, item.quantity + 1)} className="px-3 py-2 hover:bg-secondary btn-transition" data-testid={`cart-increase-${k}`} aria-label="Increase">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <button onClick={() => removeItem(k)} className="text-muted-foreground hover:text-[#E31837] btn-transition" data-testid={`cart-remove-${k}`} aria-label="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="font-bold">{formatINR(item.price * item.quantity)}</div>
              </div>
            );
          })}
        </div>

        <div className="lg:sticky lg:top-28 self-start border border-[#0A0A0A] p-6 h-fit" data-testid="cart-summary">
          <h2 className="heading-3 mb-6">Order Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{formatINR(subtotal)}</span></div>
            <div className="flex justify-between border-t border-border pt-3 mt-3 text-lg">
              <span className="font-head font-bold uppercase">Total</span>
              <span className="font-bold" data-testid="cart-total">{formatINR(total)}</span>
            </div>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            className="w-full mt-6 bg-[#E31837] text-white py-4 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-[0.98]"
            data-testid="checkout-btn"
          >
            Checkout <ArrowRight className="h-4 w-4" />
          </button>
          <Link to="/shop" className="block text-center mt-4 label-caps text-muted-foreground hover:text-[#0A0A0A] btn-transition">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
